"use client"

import type React from "react"
import { useState } from "react"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import type { Subject } from "../../../../lib/subject-data"
import { teachers } from "../../../../lib/subject-data"

interface AddSubjectModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd: (subject: Omit<Subject, "id">) => void
}

export function AddSubjectModal({ isOpen, onClose, onAdd }: AddSubjectModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    teacher: teachers[0],
    description: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name || !formData.code) {
      alert("Please fill in the course name and code.")
      return
    }

    const colors = ["orange", "purple", "green", "pink", "blue", "amber", "indigo", "red"]
    const icons = [
      "calculator",
      "languages",
      "sprout",
      "globe-2",
      "microscope",
      "message-circle",
      "computer",
      "dumbbell",
    ]

    const newSubject: Omit<Subject, "id"> = {
      name: formData.name,
      code: formData.code,
      enrolled: Math.floor(Math.random() * 50) + 70,
      teacher: formData.teacher,
      teacherAvatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.teacher}`,
      icon: icons[Math.floor(Math.random() * icons.length)],
      color: colors[Math.floor(Math.random() * colors.length)],
      description: formData.description || "Nigerian curriculum subject",
    }

    onAdd(newSubject)
    setFormData({ name: "", code: "", teacher: teachers[0], description: "" })
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-teal-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-bold text-white">Add New Subject</h3>
            <button 
              onClick={onClose} 
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal Content */}
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-6">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="courseName">
                    Subject Name
                  </label>
                  <Input
                    id="courseName"
                    name="courseName"
                    placeholder="e.g. Social Studies"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="courseCode">
                      Course Code
                    </label>
                    <Input
                      id="courseCode"
                      name="courseCode"
                      placeholder="e.g. SOC-101"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="teacher">
                      Assigned Teacher
                    </label>
                    <select
                      id="teacher"
                      name="teacher"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      value={formData.teacher}
                      onChange={(e) => setFormData({ ...formData, teacher: e.target.value })}
                    >
                      {teachers.map((teacher) => (
                        <option key={teacher} value={teacher}>
                          {teacher}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="description">
                    Course Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Brief description of the curriculum..."
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-gray-200">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="border-gray-300 hover:bg-gray-100 text-gray-700"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700 text-white focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              >
                Confirm
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}